import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Upload, Loader2, BadgeIndianRupee } from "lucide-react";

const inputCls = "w-full rounded-md border border-slate-300 px-3 py-2 text-sm";
const F = ({ label, children }) => (
  <label className="block"><span className="mb-1 block text-xs font-medium text-slate-600">{label}</span>{children}</label>
);

export default function InstallmentImport() {
  const [which, setWhich] = useState("first");
  const [status, setStatus] = useState("Paid");
  const [amount, setAmount] = useState("");
  const [sanctionDate, setSanctionDate] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [remark, setRemark] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);

  const doPreview = async () => {
    if (!file) return toast.error("Please choose an Excel, CSV or PDF list");
    setBusy(true); setSummary(null);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("which", which);
      const { data } = await api.post("/beneficiaries/installments/import/preview", fd);
      setPreview(data);
      toast.success(`${data.matched} beneficiaries matched, ${data.unmatched} not found`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/beneficiaries/installments/import/confirm", {
        which, identifiers: preview.identifiers, status,
        amount: Number(amount) || 0, sanction_date: sanctionDate, payment_date: paymentDate, remark,
      });
      setSummary(data); setPreview(null); setFile(null);
      toast.success(`${data.updated} beneficiaries marked ${data.status}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="installment-import-page">
      <div>
        <h1 className="font-serif text-3xl font-semibold text-[#0f2545] sm:text-4xl">Installment List Import</h1>
        <p className="text-sm text-slate-500">हप्ता यादी आयात · upload the paid list (Excel / CSV / PDF) and update records in bulk</p>
      </div>

      <div className="grid gap-4 rounded-xl border border-slate-200 bg-white p-5 sm:grid-cols-2 lg:grid-cols-3">
        <F label="Installment">
          <select data-testid="ii-which" value={which} onChange={(e) => { setWhich(e.target.value); setPreview(null); }} className={inputCls}>
            <option value="first">First Installment / पहिला हप्ता</option>
            <option value="second">Second Installment / दुसरा हप्ता</option>
          </select>
        </F>
        <F label="Set Status To">
          <select data-testid="ii-status" value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
            {["Paid", "Pending", "Rejected", "Under Process"].map((s) => <option key={s}>{s}</option>)}
          </select>
        </F>
        <F label="Amount per beneficiary (₹) — optional">
          <input data-testid="ii-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} placeholder="e.g. 25000" />
        </F>
        <F label="Sanction Date — optional"><input data-testid="ii-sanction" type="date" value={sanctionDate} onChange={(e) => setSanctionDate(e.target.value)} className={inputCls} /></F>
        <F label="Payment Date — optional"><input data-testid="ii-payment" type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} className={inputCls} /></F>
        <F label="Remark — optional"><input data-testid="ii-remark" value={remark} onChange={(e) => setRemark(e.target.value)} className={inputCls} placeholder="e.g. 1st installment paid list Aug 2026" /></F>
        <div className="sm:col-span-2 lg:col-span-3 flex flex-wrap items-center gap-3 border-t border-slate-100 pt-4">
          <input data-testid="ii-file" type="file" accept=".xlsx,.xls,.csv,.pdf" onChange={(e) => { setFile(e.target.files[0]); setPreview(null); }} className="text-sm" />
          <button data-testid="ii-preview-btn" disabled={busy} onClick={doPreview} className="flex items-center gap-1.5 rounded-md bg-[#0f2545] px-4 py-2 text-sm text-white disabled:opacity-60">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Match Records
          </button>
          <span className="text-xs text-slate-500">Application numbers or Beneficiary IDs are matched automatically. Every ID found in the file is updated — the paid list itself is the authority.</span>
        </div>
      </div>

      {preview && (
        <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-5" data-testid="ii-preview">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            {[["IDs in File", preview.total_found_in_file], ["Matched", preview.matched], ["Not Found", preview.unmatched], ["Not Currently Eligible", preview.not_eligible ?? 0], ["Already Paid", preview.already_paid]].map(([l, v]) => (
              <div key={l} className="rounded-lg border border-slate-200 p-3"><div className="text-xl font-semibold text-[#0f2545]">{v}</div><div className="text-xs text-slate-500">{l}</div></div>
            ))}
          </div>

          {preview.unmatched > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="ii-unmatched">
              <div className="font-medium">Not found in the database (first 100 shown) — these will be skipped:</div>
              <div className="mt-1 break-words font-mono">{preview.unmatched_list.join(", ")}</div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-[700px] w-full text-xs">
              <thead className="bg-slate-100 text-left"><tr>{["Beneficiary ID", "Application No.", "Name", "Taluka", "Eligibility", "Current Status"].map((h) => <th key={h} className="p-2">{h}</th>)}</tr></thead>
              <tbody>
                {preview.preview.map((r) => (
                  <tr key={r.beneficiary_id} className="border-t border-slate-100">
                    <td className="p-2 font-mono">{r.beneficiary_id}</td><td className="p-2 font-mono">{r.application_number}</td>
                    <td className="p-2">{r.full_name}</td><td className="p-2">{r.taluka}</td>
                    <td className="p-2">{r.eligibility_status}</td><td className="p-2">{r.current_status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button data-testid="ii-confirm-btn" disabled={busy} onClick={confirm} className="flex items-center gap-1.5 rounded-md bg-[#0f2545] px-5 py-2.5 text-sm text-white disabled:opacity-60">
            <BadgeIndianRupee size={15} /> Update {preview.matched} beneficiaries → {which === "first" ? "1st" : "2nd"} installment {status}
          </button>
        </div>
      )}

      {summary && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-800" data-testid="ii-summary">
          <div className="font-semibold">Update Completed</div>
          <div className="mt-1">{summary.updated} beneficiaries now show {summary.which === "first" ? "First" : "Second"} Installment = {summary.status}. An audit log entry has been recorded.</div>
        </div>
      )}
    </div>
  );
}
